import React from 'react';
import './ReportFullSessionPostSessionPanel.css';
import ReportFullSessionAnalytics from './ReportFullSessionAnalytics';
import ReportFullSessionMetricsSummary from './ReportFullSessionMetricsSummary';
import ReportDownloadActions from './ReportDownloadActions';
import ReportAnalysisStatusView from './ReportAnalysisStatusView';

function StartNewSessionButton({ t, onRestartSession, showCue = false }) {
  return (
    <div className="restart-session-tooltip-wrap">
      <button
        onClick={onRestartSession}
        className={`restart-session-button${showCue ? ' restart-session-button--selection-cue' : ''}`}
        data-guided-highlight-purpose={showCue ? 'Start another Full Session with a client.' : undefined}
      >
        {t('report_start_new_session_btn')}
      </button>
      <div className="restart-session-tooltip" role="status">
        You can return to this session in your account page.
      </div>
    </div>
  );
}

function ReportFullSessionPostSessionPanel({
  t,
  theme = 'light',
  analysisStatus,
  postSessionView,
  setPostSessionView,
  onViewedTurnChange,
  currentTurnMessages,
  displayStageMetrics,
  hasStageMetricsData,
  revealedItems,
  systemMessagesHistory,
  showPostDownloadNextStepCue = false,
  showDownloadCue = false,
  showDownloadNotice = false,
  onCloseDownloadNotice,
  showReturnToAccountCue = false,
  isClientMode = false,
  analyticsVisible = true,
  onToggleAnalytics,
  onRestartSession,
  showRestartAction = true,
  onReturnToAccount,
  onDownloadPdf,
  onDownloadCsv,
  AnalyticsMessageComponent,
  LoadingDotsComponent,
  actionsDetached = false,
  turnIndex = -1,
  fullSessionGuidanceVisible = false,
  onRevealFullSessionGuidance,
  onHideFullSessionGuidance,
  reportSessionId = null,
}) {
  if (!analysisStatus) {
    return null;
  }

  if (postSessionView === 'analytics') {
    return (
      <ReportFullSessionAnalytics
        t={t}
        title={t('report_post_session_title')}
        currentTurnMessages={currentTurnMessages}
        displayStageMetrics={displayStageMetrics}
        hasStageMetricsData={hasStageMetricsData}
        conversationStarted
        isClientMode={isClientMode}
        showClientParameters={isClientMode}
        revealedItems={revealedItems}
        analyticsVisible={analyticsVisible}
        onToggleAnalytics={onToggleAnalytics}
        showVisibilityToggle
        emptyScoringMessage={t('report_no_data_this_turn')}
        emptyMetricsMessage={t('report_no_metrics_available')}
        AnalyticsMessageComponent={AnalyticsMessageComponent}
        LoadingDotsComponent={LoadingDotsComponent}
        onBackToMetrics={() => {
          setPostSessionView('metrics');
          onViewedTurnChange(null);
        }}
        turnIndex={turnIndex}
        guidanceVisible={fullSessionGuidanceVisible}
        onRevealGuidance={onRevealFullSessionGuidance}
        onHideGuidance={onHideFullSessionGuidance}
        reportSessionId={reportSessionId}
      />
    );
  }

  const analysisRunning = ['analyzing', 'computing_metrics', 'generating_summary'].includes(analysisStatus.status);
  const showMetricsBody = isClientMode
    ? analysisStatus.status === 'completed'
    : Boolean(analysisStatus.metrics);

  // Metrics are computed within moments of session end, well before the MITI
  // global scoring and PDF writing finish — once they exist, show the metrics
  // page with the "writing report" download state instead of a bare
  // "Analyzing" placeholder. Client mode keeps the placeholder (its analysis
  // completes almost immediately anyway).
  if (!showMetricsBody && analysisRunning) {
    return <ReportAnalysisStatusView t={t} variant="analyzing" />;
  }

  if (isClientMode && analysisStatus.status === 'completed') {
    if (!showRestartAction && onReturnToAccount) {
      return (
        <div className="post-session-metrics-layout">
          <div className="post-session-metrics-body">
            <ReportFullSessionMetricsSummary t={t} metrics={analysisStatus.metrics} endReason={analysisStatus.end_reason} />
            <ReportDownloadActions t={t} isClientMode onDownloadCsv={onDownloadCsv} />
          </div>
        </div>
      );
    }

    return (
      <div className="post-session-metrics-layout">
        <div className="post-session-metrics-body">
          <ReportFullSessionMetricsSummary t={t} metrics={analysisStatus.metrics} endReason={analysisStatus.end_reason} />
          <ReportDownloadActions t={t} isClientMode onDownloadCsv={onDownloadCsv} />
          {!actionsDetached && systemMessagesHistory && systemMessagesHistory.length > 0 && (
            <div className="review-analytics-container">
              <button
                onClick={() => setPostSessionView('analytics')}
                className="review-analytics-button"
              >
                {t('report_review_analytics_btn')}
              </button>
            </div>
          )}
          {!actionsDetached && showRestartAction ? (
            <div className="restart-session-container">
              <StartNewSessionButton
                t={t}
                onRestartSession={onRestartSession}
                showCue={showPostDownloadNextStepCue}
              />
            </div>
          ) : !actionsDetached && onReturnToAccount ? (
            <div className="restart-session-container">
              <button
                onClick={onReturnToAccount}
                className={`restart-session-button${showReturnToAccountCue ? ' restart-session-button--selection-cue' : ''}`}
                data-guided-highlight-purpose={showReturnToAccountCue ? 'Return to Account to review this saved session.' : undefined}
              >
                {t('account_return_to_account')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (showMetricsBody) {
    if (!showRestartAction && onReturnToAccount) {
      return (
        <div className="post-session-metrics-layout">
          <div className="post-session-metrics-body">
            <ReportFullSessionMetricsSummary t={t} metrics={analysisStatus.metrics} smokerMetrics={analysisStatus.smoker_metrics} endReason={analysisStatus.end_reason} />

            {!isClientMode && (analysisRunning || analysisStatus.status === 'generating_pdf' || analysisStatus.pdf_complete) && (
              <ReportDownloadActions
                t={t}
                theme={theme}
                showDownloadCue={showDownloadCue}
                showDownloadNotice={showDownloadNotice}
                onCloseDownloadNotice={onCloseDownloadNotice}
                pdfComplete={analysisStatus.pdf_complete}
                onDownloadPdf={onDownloadPdf}
                onDownloadCsv={onDownloadCsv}
              />
            )}

            {analysisStatus.pdf_error && (
              <div className="progress-placeholder">
                <p style={{ color: '#ef4444' }}>{`${t('report_pdf_error_prefix')}${analysisStatus.pdf_error}`}</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="post-session-metrics-layout">
        <div className="post-session-metrics-body">
          <ReportFullSessionMetricsSummary t={t} metrics={analysisStatus.metrics} smokerMetrics={analysisStatus.smoker_metrics} endReason={analysisStatus.end_reason} />

          {!isClientMode && (analysisRunning || analysisStatus.status === 'generating_pdf' || analysisStatus.pdf_complete) && (
            <ReportDownloadActions
              t={t}
              theme={theme}
              showDownloadCue={showDownloadCue}
              showDownloadNotice={showDownloadNotice}
              onCloseDownloadNotice={onCloseDownloadNotice}
              pdfComplete={analysisStatus.pdf_complete}
              onDownloadPdf={onDownloadPdf}
              onDownloadCsv={onDownloadCsv}
            />
          )}

          {analysisStatus.pdf_error && (
            <div className="progress-placeholder">
              <p style={{ color: '#ef4444' }}>{`${t('report_pdf_error_prefix')}${analysisStatus.pdf_error}`}</p>
            </div>
          )}

          {!actionsDetached && analysisStatus.pdf_complete && systemMessagesHistory && systemMessagesHistory.length > 0 && (
            <div className="review-analytics-container">
              <button
                onClick={() => setPostSessionView('analytics')}
                className="review-analytics-button"
              >
                {t('report_review_analytics_btn')}
              </button>
            </div>
          )}

          {!actionsDetached && analysisStatus.pdf_complete && showRestartAction ? (
            <div className="restart-session-container">
              <StartNewSessionButton
                t={t}
                onRestartSession={onRestartSession}
                showCue={showPostDownloadNextStepCue}
              />
            </div>
          ) : !actionsDetached && analysisStatus.pdf_complete && onReturnToAccount ? (
            <div className="restart-session-container">
              <button
                onClick={onReturnToAccount}
                className={`restart-session-button${showReturnToAccountCue ? ' restart-session-button--selection-cue' : ''}`}
                data-guided-highlight-purpose={showReturnToAccountCue ? 'Return to Account to review this saved session.' : undefined}
              >
                {t('account_return_to_account')}
              </button>
            </div>
          ) : null}

          {!actionsDetached && !analysisStatus.pdf_complete && systemMessagesHistory && systemMessagesHistory.length > 0 && (
            <div className="review-analytics-container">
              <button
                onClick={() => setPostSessionView('analytics')}
                className="review-analytics-button"
              >
                {t('report_review_analytics_btn')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (analysisStatus.automisc_error) {
    return <ReportAnalysisStatusView t={t} variant="error" error={analysisStatus.automisc_error} />;
  }

  return <ReportAnalysisStatusView t={t} variant="completed" />;
}

export default ReportFullSessionPostSessionPanel;
